'use client'
import { createContext, useContext, useState, ReactNode } from 'react'
import { cn } from '../../helpers/classnames'

type JosefTabsVariant = 'underline' | 'pill'

interface TabsContextType {
  activeTab: string
  setActiveTab: (tab: string) => void
  variant: JosefTabsVariant
}

const TabsContext = createContext<TabsContextType | undefined>(undefined)

interface JosefTabsProps {
  defaultValue?: string
  value?: string
  onValueChange?: (value: string) => void
  /** Visual style. `underline` (default) is the standard app tab look. */
  variant?: JosefTabsVariant
  children: ReactNode
  className?: string
}

interface JosefTabsListProps {
  children: ReactNode
  className?: string
}

interface JosefTabsTriggerProps {
  value: string
  children: ReactNode
  className?: string
  onClick?: () => void
}

interface JosefTabsContentProps {
  value: string
  children: ReactNode
  className?: string
}

export function JosefTabs({
  defaultValue,
  value,
  onValueChange,
  variant = 'underline',
  children,
  className,
}: JosefTabsProps) {
  const [internalValue, setInternalValue] = useState(
    defaultValue ?? value ?? ''
  )

  const activeTab = value ?? internalValue

  const setActiveTab = (tab: string) => {
    if (value === undefined) {
      setInternalValue(tab)
    }
    onValueChange?.(tab)
  }

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab, variant }}>
      <div className={cn('w-full', className)}>{children}</div>
    </TabsContext.Provider>
  )
}

export function JosefTabsList({ children, className }: JosefTabsListProps) {
  const context = useContext(TabsContext)
  const variant = context?.variant ?? 'underline'

  if (variant === 'pill') {
    return (
      <div
        className={cn(
          'inline-flex h-11 items-stretch rounded-lg border border-black/10 bg-black/5 p-1 dark:border-white/10 dark:bg-white/5',
          className
        )}
      >
        {children}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-stretch gap-1 border-b border-black/10 dark:border-white/10',
        className
      )}
    >
      {children}
    </div>
  )
}

export function JosefTabsTrigger({
  value,
  children,
  className,
  onClick,
}: JosefTabsTriggerProps) {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('JosefTabsTrigger must be used within JosefTabs')
  }

  const { activeTab, setActiveTab, variant } = context
  const isActive = activeTab === value

  const handleClick = () => {
    setActiveTab(value)
    onClick?.()
  }

  if (variant === 'pill') {
    return (
      <button
        type="button"
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/20 dark:focus-visible:ring-white/20',
          'disabled:pointer-events-none disabled:opacity-50',
          isActive
            ? 'border border-black/10 bg-white text-black shadow-sm dark:border-white/10 dark:bg-black dark:text-white'
            : 'text-black/60 hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white',
          className
        )}
        onClick={handleClick}
      >
        {children}
      </button>
    )
  }

  return (
    <button
      type="button"
      className={cn(
        '-mb-px inline-flex items-center justify-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors',
        'focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-50',
        isActive
          ? 'border-black text-black dark:border-white dark:text-white'
          : 'border-transparent text-black/55 hover:border-black/20 hover:text-black dark:text-white/55 dark:hover:border-white/20 dark:hover:text-white',
        className
      )}
      onClick={handleClick}
    >
      {children}
    </button>
  )
}

export function JosefTabsContent({
  value,
  children,
  className,
}: JosefTabsContentProps) {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('JosefTabsContent must be used within JosefTabs')
  }

  const { activeTab } = context

  if (activeTab !== value) {
    return null
  }

  return (
    <div
      className={cn(
        'ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2',
        className
      )}
    >
      {children}
    </div>
  )
}
